import { StatutCommandeLabels } from "../../../../models/commandeStatut.map";
import { PaperclipIcon } from "../../../../components/icons/InterfaceIcons";
import { attachmentCount as countAttachments, attachmentLabel, destinationCounts, formatReception, plural } from "../../model/commandeListFormat";
import { hiddenSearchMatches } from "../../model/commandeListSearch";
import { commandeRowColors, commandeSender, statusConfigOf } from "../../model/commandeListAppearance";
import { CommandeSummaryPopover } from "./CommandeSummaryPopover";
import { HighlightSearchText, SearchMatchesButton, StarButton } from "./CommandeRowParts";

const TEXT_BUTTON_STYLE = { border: 0, padding: 0, background: "transparent", fontFamily: "inherit", textAlign: "left", cursor: "pointer" };

function MailRowCounts({ cmd }) {
    const patients = cmd.patients ?? [];
    const { robot, etiquetage } = destinationCounts(patients);
    const attachments = countAttachments(cmd);
    const label = attachmentLabel(attachments);
    return (
        <div className="commande-mail-counts">
            <CommandeSummaryPopover patients={patients}>
                <span className="commande-count commande-count--patients">
                    {patients.length} patient{plural(patients.length)}
                </span>
                <span className="commande-count commande-count--robot">Robot {robot}</span>
                <span className="commande-count commande-count--etiquetage">Étiq. {etiquetage}</span>
            </CommandeSummaryPopover>
            <span className="commande-count commande-count--attachments" title={label} aria-label={label}>
                <PaperclipIcon size={14} /> {attachments}
            </span>
            {cmd.lockedBy && (
                <span className="commande-count commande-count--locked" title={cmd.lockedBy}>
                    🔒 En cours de traitement · <span>{cmd.lockedBy}</span>
                </span>
            )}
        </div>
    );
}

function MailRowContent({ cmd, isRead, senderName, searchQuery, onSelect }) {
    return (
        <div className="commande-mail-content">
            <button type="button" className="commande-mail-subject" title={cmd.objet || "Sans objet"} onClick={onSelect} style={TEXT_BUTTON_STYLE}>
                <span className="commande-mail-subject-text">
                    <HighlightSearchText value={cmd.objet || "(Sans objet)"} query={searchQuery} />
                </span>
                {isRead && <span className="commande-read-label">Lu</span>}
            </button>
            {cmd.emailEmetteur && (
                <button type="button" className="commande-mail-email" title={cmd.emailEmetteur} onClick={onSelect} style={TEXT_BUTTON_STYLE}>
                    <HighlightSearchText value={cmd.emailEmetteur} query={searchQuery} />
                </button>
            )}
            <SearchMatchesButton
                searchMatches={hiddenSearchMatches(cmd, searchQuery, senderName)}
                searchQuery={searchQuery}
                onClick={onSelect}
            />
            <MailRowCounts cmd={cmd} />
        </div>
    );
}

function MailRowMeta({ cmd, senderName, statusConfig, isSelected, showStatut, onSelect, onEdit }) {
    const { time: recTime } = formatReception(cmd.dateReception);
    return (
        <div className="commande-mail-meta">
            <div className="commande-mail-time">{recTime || "—"}</div>
            {showStatut && (
                <span className="commande-mail-status" style={{ color: statusConfig.color }}>
                    <span style={{ background: statusConfig.dot }} />
                    {StatutCommandeLabels[cmd.statut] || cmd.statut}
                </span>
            )}
            {onEdit && (
                <button
                    type="button"
                    className={`commande-row-edit${isSelected ? " commande-row-edit--selected" : ""}`}
                    aria-label={`Éditer la commande ${cmd.objet || senderName}`}
                    onClick={event => {
                        event.stopPropagation();
                        onSelect();
                        onEdit(cmd);
                    }}
                >
                    <span>Éditer</span>
                    {isSelected && <kbd title="Raccourci clavier : Entrée" aria-label="Entrée">↵</kbd>}
                </button>
            )}
        </div>
    );
}

// Ligne d'une commande dans la boîte de réception : expéditeur, objet, compteurs, statut et édition.
export function CommandeMailRow({ cmd, idx, flags, showStatut, searchQuery, handlers }) {
    const { isRead, isFavorite, isFocused, isSelected } = flags;
    const { onContextMenu, onStarClick, onRowClick, onEdit } = handlers;
    const { name: senderName, colors: avatarColors } = commandeSender(cmd);
    const statusConfig = statusConfigOf(cmd.statut);
    const colors = commandeRowColors({
        isRead,
        isFocused,
        isSelected,
        isLocked: Boolean(cmd.lockedBy),
        isValide: showStatut && cmd.statut === "VALIDE",
        statusRail: statusConfig.rail,
    });
    const select = () => onRowClick(cmd, idx);
    const selectFromControl = event => {
        event.stopPropagation();
        select();
    };

    return (
        <div
            className={`commande-mail-row${isRead ? " commande-mail-row--read" : ""}`}
            aria-current={isSelected ? "true" : undefined}
            onClick={select}
            onContextMenu={event => onContextMenu(event, cmd)}
            style={{ "--commande-rail": colors.rail, background: colors.background, boxShadow: colors.boxShadow }}
            onMouseEnter={event => {
                if (!isFocused && !isSelected) event.currentTarget.style.background = colors.hover;
            }}
            onMouseLeave={event => { event.currentTarget.style.background = colors.background; }}
        >
            <StarButton active={isFavorite} onClick={event => onStarClick(event, cmd)} />

            <button
                type="button"
                onClick={selectFromControl}
                aria-label={`Sélectionner la commande de ${senderName}`}
                data-testid={`ehpad-avatar-${cmd.id}`}
                title={senderName}
                className="commande-mail-sender"
                style={{ background: avatarColors.bg, color: avatarColors.color, border: 0, fontFamily: "inherit", cursor: "pointer" }}
            >
                <HighlightSearchText value={senderName} query={searchQuery} />
            </button>

            <MailRowContent cmd={cmd} isRead={isRead} senderName={senderName} searchQuery={searchQuery} onSelect={selectFromControl} />
            <MailRowMeta
                cmd={cmd}
                senderName={senderName}
                statusConfig={statusConfig}
                isSelected={isSelected}
                showStatut={showStatut}
                onSelect={select}
                onEdit={onEdit}
            />
        </div>
    );
}
